import type {
  TrackingLink,
  TrackingLinkExperience,
  TrackingLinkStatus,
} from "../types";

export const TRACKING_LINK_BASE_URL = (
  import.meta.env.VITE_TRACKING_LINK_BASE_URL || "https://go.echoflicks.com"
).replace(/\/$/, "");

export const RESERVED_TRACKING_LINK_SLUGS = new Set([
  "admin",
  "api",
  "favicon",
  "health",
  "login",
  "privacy",
  "robots",
  "terms",
  "_next",
]);

export const TRACKING_LINK_SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;
export const TRACKING_LINK_CODE_RE = /^EF-[A-F0-9]{8}$/;

export const TRACKING_LINK_UTM_EXAMPLE = {
  source: "linkedin",
  medium: "social",
  campaign: "echoflicks-linked-post",
  content: "main-post",
} as const;

export function slugifyTrackingLink(value: string): string {
  return value
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/g, "");
}

export function trackingLinkSlugError(slug: string): string | null {
  if (RESERVED_TRACKING_LINK_SLUGS.has(slug)) return "This slug is reserved.";
  if (!TRACKING_LINK_SLUG_RE.test(slug)) {
    return "Use 3–64 lowercase letters, numbers, or hyphens; start and end with a letter or number.";
  }
  return null;
}

export type TrackingLinkSlugAvailability =
  | "available"
  | "live_conflict"
  | "archived_reuse";

/**
 * A slug is owned by at most one live (active/paused) link; archived links
 * release it. Reusing a released slug never mixes analytics: clicks and
 * conversions key on each link row's immutable id / EF code, so the new link
 * starts from zero and the archived link keeps its history.
 */
export function trackingLinkSlugAvailability(
  slug: string,
  links: readonly Pick<TrackingLink, "slug" | "status">[],
): TrackingLinkSlugAvailability {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) return "available";
  let archived = false;
  for (const link of links) {
    if (link.slug.toLowerCase() !== normalized) continue;
    if ((link.status as TrackingLinkStatus) !== "archived") {
      return "live_conflict";
    }
    archived = true;
  }
  return archived ? "archived_reuse" : "available";
}

export function trackingLinkUrl(slug: string): string {
  return `${TRACKING_LINK_BASE_URL}/${encodeURIComponent(slug)}`;
}

export function trackingLinkPreviewUrl(slug: string): string {
  const url = new URL(trackingLinkUrl(slug));
  url.searchParams.set("preview", "crm");
  return url.toString();
}

export function trackingLinkMessage(
  _experience: TrackingLinkExperience,
  publicCode = "EF-XXXXXXXX",
): string {
  return `Hi Felix! I am sending you my code for a free video: ${publicCode}`;
}

export function trackingLinkSource(publicCode: string): string {
  return `link:${publicCode.toUpperCase()}`;
}

export function codeFromTrackingLinkSource(
  source?: string | null,
): string | null {
  if (!source?.startsWith("link:")) return null;
  const code = source.slice("link:".length).toUpperCase();
  return TRACKING_LINK_CODE_RE.test(code) ? code : null;
}

export function trackingLinkName(
  code: string | null | undefined,
  links: readonly Pick<TrackingLink, "public_code" | "name">[],
): string | null {
  if (!code) return null;
  return (
    links.find((link) => link.public_code.toUpperCase() === code.toUpperCase())
      ?.name ?? null
  );
}
